/* @flow */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ReactAccessConsumer } from 'react-access';

import NavigationService from 'services/NavigationService';
import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import * as accountPermissions from 'permissions/account';
import * as generalPermissions from 'permissions/general';
import * as reportingPermissions from 'permissions/reporting';
import TableButton from 'components/TableButton';
import { actionDrawerStore } from 'services/ServiceRegistry';
import { formulateUri } from 'tables/components/ReportView/state';

import createReportView from '../components/ReportView/ReportViewBase';
import type { ViewConfigGetter } from '../components/ReportView/ReportViewBase';
import type {
  ViewConfig,
  CellComponentProps,
  DataViewColumn,
} from '../components/ReportView/types';

const NameCell = ({ display, rowData }: { display: string, rowData: Object }) => (
  <ReactAccessConsumer>
    {isAuthed => {
      if (isAuthed(generalPermissions.userManagement)) {
        return (
          <Link to={`/users/profile/${rowData.users_id}`}>
            <span>{display}</span>
          </Link>
        );
      }

      if (isAuthed(accountPermissions.readUser)) {
        return (
          <Link to={`/accounts/${rowData.account_id}/user/${rowData.users_id}`}>
            <span>{display}</span>
          </Link>
        );
      }

      return display;
    }}
  </ReactAccessConsumer>
);

const columns: DataViewColumn[] = [
  {
    columnName: 'first_name',
    title: 'general:reporting.account.labels.first_name',
    flexBasis: 140,
    component: NameCell,
  },
  {
    columnName: 'last_name',
    title: 'general:reporting.account.labels.last_name',
    flexBasis: 140,
    component: NameCell,
  },
  {
    columnName: 'role_id',
    title: 'general:reporting.account.labels.role_name',
    noFlexGrow: true,
    flexBasis: 120,
    component: function RoleNameCell({ display, t }) {
      return t(`general:roles.${display}`);
    },
  },
  {
    columnName: 'user_is_enabled',
    title: 'general:reporting.account.labels.status',
    flexBasis: 160,
    noFlexGrow: true,
    component: function StatusCell({ display }) {
      return <StatusIndicator status={Boolean(display)} />;
    },
  },
  {
    columnName: 'user_date_added',
    title: 'general:reporting.account.labels.date_added',
    flexBasis: 120,
  },
  {
    columnName: 'er_users_last_login_date',
    title: 'general:reporting.account.labels.last_login_date',
    flexBasis: 120,
  },
  {
    columnName: 'er_users_login_count',
    title: 'general:reporting.account.labels.total_logins',
    noFlexGrow: true,
    flexBasis: 120,
  },
  {
    columnName: 'er_users_login_current_year_count',
    title: 'general:reporting.account.labels.total_logins_ytd',
    noFlexGrow: true,
    flexBasis: 120,
  },
  {
    columnName: 'er_users_login_current_month_count',
    title: 'general:reporting.account.labels.total_logins_mtd',
    noFlexGrow: true,
    flexBasis: 120,
  },
];

const getViewConfig: ViewConfigGetter = (props, _, getters) => ({
  title: 'general:reporting.account.title',
  codename: 'reporting-account',
  type: 'SINGLE_WITH_SUMMARY',
  userFilterNamespace: 'Users',
  disableFilters: true,
  disableUserPreferenceSave: true,
  disableFilterChips: true,
  downloadPermissions: reportingPermissions.downloadUsers,
  view: {
    dataPathname: 'user-stats',
    apiType: 'SQL',
    columns,
  },
  summaryView: {
    title: {
      dependencies: ['routing', 'brandStore', 'accountStore'],
      compute: data => {
        return [
          { text: 'general:reporting.brands.summary.title', to: '/reporting' },
          {
            text:
              data.brandStore && !data.brandStore.brandLoading && data.brandStore.selectedBrand
                ? data.brandStore.selectedBrand.name
                : 'Loading...',
            loading: !data.brandStore || data.brandStore.brandLoading,
            to:
              data.brandStore && data.brandStore.selectedBrand
                ? `/reporting/brand/${data.brandStore.selectedBrand.id}`
                : undefined,
          },
          {
            text:
              data.accountStore &&
              !data.accountStore.loadingDetails &&
              data.accountStore.details &&
              data.accountStore.details.details
                ? data.accountStore.details.details.name
                : 'Loading...',
            loading: !data.accountStore || data.accountStore.loadingDetails,
          },
        ];
      },
    },
    sectionHeading: 'general:reporting.account.summary.section_heading',
    dataPathname: 'account-stats',
    excludeFilterKeysFromReportView: ['region_id'],
    useUriTokens: ['accountId'],
    logoPath: ['props', 'brandStore', 'selectedBrand', 'logo'],
    buttons: [
      {
        label: 'View Account Detail',
        icon: '#icon-list-alt',
        to: `/accounts/${props.match.params.accountId}`,
        position: 'left',
        permissions: accountPermissions.read,
      },
    ],
    sectionHeadingButtons: [
      {
        label: 'general:actions.export_summary',
        icon: '#icon-download-page',
        onClick: () => {
          const { accountId, brandId, region } = getters.getSummaryViewInfo();

          props.reportViewStore.exportSummary('account-stats', { region, accountId });
        },
        permissions: reportingPermissions.viewAllBrands,
      },
    ],
    mapDataToLayout: data => {
      const [targetItem] = data;

      const {
        er_contracts_open_count,
        er_contracts_closed_count,
        er_assets_total_rental_count,
        er_assets_on_sold_count,
        er_assets_owned_count,
        er_account_users_count,
        er_account_projects_count,
        er_account_projects_current_year_count,
        er_account_projects_current_month_count,
        er_account_cost_centres_count,
        er_account_cost_centres_current_year_count,
        er_account_cost_centres_current_month_count,
        er_account_users_unique_login_count,
        er_account_users_login_count,
        er_account_users_current_month_unique_login_count,
        er_account_users_current_year_unique_login_count,
        er_account_users_current_month_login_count,
        er_account_users_current_year_login_count,

        request_contract_extension_count,
        request_contract_extension_current_month_count,
        request_contract_extension_current_year_count,

        request_asset_rental_extension_count,
        request_asset_rental_extension_current_month_count,
        request_asset_rental_extension_current_year_count,

        quote_request_count,
        quote_request_current_month_count,
        quote_request_current_year_count,

        new_asset_request_count,
        new_asset_request_current_year_count,
        new_asset_request_current_month_count,

        new_license_request_count,
        new_license_request_current_year_count,
        new_license_request_current_month_count,

        asset_calibration_request_count,
        asset_calibration_request_current_month_count,
        asset_calibration_request_current_year_count,

        asset_fault_count,
        asset_fault_current_month_count,
        asset_fault_current_year_count,
      } = targetItem;

      const layout = [
        [
          {
            title: 'general:reporting.account.summary.main_card_title',
            columns: [{ heading: 'general:reporting.summary_titles.summary' }],
            data: [
              {
                label: 'general:reporting.summary_labels.open_contracts',
                data: [er_contracts_open_count],
              },
              {
                label: 'general:reporting.summary_labels.closed_contracts',
                data: [er_contracts_closed_count],
              },
              {
                label: 'general:reporting.summary_labels.rental_assets',
                data: [er_assets_total_rental_count],
              },
              {
                label: 'general:reporting.summary_labels.sale_assets',
                data: [er_assets_on_sold_count],
              },
              {
                label: 'general:reporting.summary_labels.owned_assets',
                data: [er_assets_owned_count],
              },
            ],
          },
        ],
        [
          {
            title: 'general:reporting.summary_titles.usage',
            columns: [
              { heading: 'general:reporting.summary_titles.total' },
              { heading: 'general:reporting.summary_titles.ytd' },
              { heading: 'general:reporting.summary_titles.mtd' },
            ],
            data: [
              {
                label: 'general:reporting.summary_labels.users',
                data: [er_account_users_count, '-', '-'],
              },
              {
                label: 'general:reporting.summary_labels.users_login',
                data: [
                  er_account_users_login_count,
                  er_account_users_current_year_login_count,
                  er_account_users_current_month_login_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.users_unique_login',
                data: [
                  er_account_users_unique_login_count,
                  er_account_users_current_year_unique_login_count,
                  er_account_users_current_month_unique_login_count,
                ],
              },
            ],
          },
          {
            title: 'general:reporting.summary_titles.process_requests',
            columns: [
              { heading: 'general:reporting.summary_titles.total' },
              { heading: 'general:reporting.summary_titles.ytd' },
              { heading: 'general:reporting.summary_titles.mtd' },
            ],
            data: [
              {
                label: 'general:reporting.summary_labels.asset_request_submissions',
                data: [
                  new_asset_request_count,
                  new_asset_request_current_year_count,
                  new_asset_request_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.license_request_submissions',
                data: [
                  new_license_request_count,
                  new_license_request_current_year_count,
                  new_license_request_current_month_count,
                ],
              },
            ],
          },
        ],
        [
          {
            title: 'general:reporting.summary_titles.customer_created_content',
            columns: [
              { heading: 'general:reporting.summary_titles.total' },
              { heading: 'general:reporting.summary_titles.ytd' },
              { heading: 'general:reporting.summary_titles.mtd' },
            ],
            data: [
              {
                label: 'general:reporting.summary_labels.projects',
                data: [
                  er_account_projects_count,
                  er_account_projects_current_year_count,
                  er_account_projects_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.cost_centres',
                data: [
                  er_account_cost_centres_count,
                  er_account_cost_centres_current_year_count,
                  er_account_cost_centres_current_month_count,
                ],
              },
            ],
          },
          {
            title: 'general:reporting.summary_titles.asset_activity',
            columns: [
              { heading: 'general:reporting.summary_titles.total' },
              { heading: 'general:reporting.summary_titles.ytd' },
              { heading: 'general:reporting.summary_titles.mtd' },
            ],
            data: [
              {
                label: 'general:reporting.summary_labels.contract_extensions',
                data: [
                  request_contract_extension_count,
                  request_contract_extension_current_year_count,
                  request_contract_extension_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.asset_extensions',
                data: [
                  request_asset_rental_extension_count,
                  request_asset_rental_extension_current_year_count,
                  request_asset_rental_extension_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.quote_requests',
                data: [
                  quote_request_count,
                  quote_request_current_year_count,
                  quote_request_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.calibration_requests',
                data: [
                  asset_calibration_request_count,
                  asset_calibration_request_current_year_count,
                  asset_calibration_request_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.faults_reported',
                data: [
                  asset_fault_count,
                  asset_fault_current_year_count,
                  asset_fault_current_month_count,
                ],
              },
            ],
          },
        ],
      ];

      return layout;
    },
  },
  buttons: [
    {
      label: 'New',
      icon: '#icon-add',
      to: '/users/create',
      permissions: accountPermissions.createInviteUser,
    },
  ],
});

export default createReportView(getViewConfig);
